import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { 
  getGroupDetails, 
  getConnections, 
  inviteFriendsToGroup 
} from '../../services/inviteService';
import { 
  FaUserFriends, 
  FaSearch, 
  FaSpinner, 
  FaCheck, 
  FaTimes,
  FaUsers,
  FaSync,
  FaArrowLeft,
  FaEnvelope,
  FaInfoCircle,
  FaExclamationTriangle
} from 'react-icons/fa';

// Helper function to extract groupId from URL (works with HashRouter)
const extractGroupIdFromURL = () => {
  try {
    const url = window.location.href;
    const hash = window.location.hash;
    
    console.log('🔍 [URL PARSER] Full URL:', url);
    console.log('🔍 [URL PARSER] Hash:', hash);
    
    // Try multiple patterns to extract groupId
    const patterns = [
      // Pattern 1: /#/groups/:groupId/invite-friends (HashRouter)
      /#\/groups\/([a-fA-F0-9]{24})\/invite-friends/,
      // Pattern 2: /groups/:groupId/invite-friends (BrowserRouter)
      /\/groups\/([a-fA-F0-9]{24})\/invite-friends/,
      // Pattern 3: Just find any 24-char hex string after /groups/
      /groups\/([a-fA-F0-9]{24})/
    ];
    
    for (const pattern of patterns) {
      const match = url.match(pattern) || hash.match(pattern);
      if (match && match[1]) {
        const groupId = match[1];
        console.log('✅ [URL PARSER] Extracted groupId:', groupId);
        console.log('✅ [URL PARSER] Using pattern:', pattern.toString());
        return groupId;
      }
    }
    
    console.warn('⚠️ [URL PARSER] No groupId found in URL');
    return null;
    
  } catch (error) {
    console.error('❌ [URL PARSER] Error extracting groupId:', error);
    return null;
  }
};

const InviteFriendsToTrip = () => {
  // Try to get groupId from multiple sources
  const paramsGroupId = useParams().groupId; // From react-router params
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);
  
  // State for groupId with fallback logic
  const [groupId, setGroupId] = useState(() => {
    // Priority 1: URL params (if available)
    if (paramsGroupId && paramsGroupId !== 'undefined' && paramsGroupId !== 'null') {
      console.log('✅ [INIT] Using groupId from useParams:', paramsGroupId);
      return paramsGroupId;
    }
    
    // Priority 2: Extract from URL manually (for HashRouter)
    const extractedId = extractGroupIdFromURL();
    if (extractedId) {
      console.log('✅ [INIT] Using groupId from URL extraction:', extractedId);
      return extractedId;
    }
    
    // Priority 3: localStorage (fallback)
    const savedGroupId = localStorage.getItem('currentGroupId') || 
                         localStorage.getItem('lastGroupId');
    if (savedGroupId && savedGroupId !== 'undefined' && savedGroupId !== 'null') {
      console.log('📦 [INIT] Using groupId from localStorage:', savedGroupId);
      return savedGroupId;
    }
    
    console.error('❌ [INIT] No valid groupId found');
    return null;
  });
  
  const [connections, setConnections] = useState([]);
  const [selectedConnections, setSelectedConnections] = useState([]);
  const [groupDetails, setGroupDetails] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadingConnections, setLoadingConnections] = useState(false);
  const [sendingInvitations, setSendingInvitations] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // Validate and save groupId on mount
  useEffect(() => {
    console.log('🔍 [MOUNT] Current groupId:', groupId);
    console.log('🔍 [MOUNT] Type:', typeof groupId);
    
    if (!groupId) {
      console.error('❌ [MOUNT] No groupId found');
      setError('Invalid trip link. Please select a trip first.');
      setTimeout(() => {
        navigate('/#/groups/my-groups');
      }, 2000);
      return;
    }
    
    // Validate groupId format
    const isValidObjectId = /^[0-9a-fA-F]{24}$/.test(groupId);
    if (!isValidObjectId) {
      console.error('❌ [MOUNT] Invalid groupId format:', groupId);
      setError('Invalid trip ID format. Please try again.');
      return;
    }
    
    console.log('✅ [MOUNT] Valid groupId confirmed');
    // Save for future use
    localStorage.setItem('currentGroupId', groupId);
  }, [groupId, navigate]);

  // Fetch group details and connections
  useEffect(() => {
    const fetchData = async () => {
      // Don't fetch if no valid groupId
      if (!groupId || !/^[0-9a-fA-F]{24}$/.test(groupId)) {
        console.error('❌ [FETCH] Invalid groupId, skipping fetch');
        return;
      }
      
      try {
        setLoading(true);
        setError('');
        
        console.log('📡 [FETCH] Fetching data for groupId:', groupId);
        
        // Fetch group details
        const groupData = await getGroupDetails(groupId);
        
        if (groupData.success && groupData.data) {
          setGroupDetails(groupData.data);
          console.log('✅ [FETCH] Group details loaded:', groupData.data.destination);
        } else {
          throw new Error(groupData.message || 'Failed to load group details');
        }
        
        // Fetch connections
        await loadConnections();
        
      } catch (error) {
        console.error('❌ [FETCH] Error:', error.message);
        
        // User-friendly error messages
        if (error.message.includes('404') || error.message.includes('not found')) {
          setError('Trip not found. It may have been deleted.');
        } else if (error.message.includes('Cast to ObjectId')) {
          setError('Invalid trip ID. Please go back and try again.');
        } else {
          setError(error.message || 'Failed to load trip details. Please try again.');
        }
        
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [groupId]);

  // Load connections function
  const loadConnections = async () => {
    try {
      console.log('📡 [CONNECTIONS] Loading connections...');
      setLoadingConnections(true);
      setError('');
      
      const connectionsData = await getConnections();
      
      console.log('✅ [CONNECTIONS] Response:', connectionsData);
      
      if (connectionsData && Array.isArray(connectionsData)) {
        console.log(`✅ [CONNECTIONS] Found ${connectionsData.length} connections`);
        
        // Process connections with proper formatting
        const processedConnections = connectionsData.map(conn => ({
          _id: conn._id || conn.id,
          id: conn.id || conn._id,
          name: conn.name || conn.username || 'Unknown User',
          username: conn.username || '',
          email: conn.email || '',
          profilePicture: conn.profilePicture || 
            `https://ui-avatars.com/api/?name=${encodeURIComponent(conn.name || conn.username || 'User')}&background=3b82f6&color=fff&size=128`,
          city: conn.city || '',
          state: conn.state || '',
          type: conn.type || 'connection'
        }));
        
        console.log('✅ [CONNECTIONS] Processed connections:', processedConnections);
        setConnections(processedConnections);
        
      } else {
        console.warn('⚠️ [CONNECTIONS] No connections array received');
        setConnections([]);
      }
      
    } catch (error) {
      console.error('❌ [CONNECTIONS] Error:', error);
      setError('Failed to load connections. Please try again.');
      setConnections([]);
    } finally {
      setLoadingConnections(false);
    }
  };

  // Filter out users already in the group
  const alreadyInGroup = useMemo(() => {
    if (!groupDetails?.currentMembers || !Array.isArray(connections)) return [];
    
    const groupMemberIds = groupDetails.currentMembers.map(member => 
      member.user?._id?.toString() || member.user?.toString() || ''
    ).filter(id => id);
    
    return connections.filter(connection => {
      const connectionId = connection._id?.toString();
      return connectionId && groupMemberIds.includes(connectionId);
    });
  }, [connections, groupDetails]);

  // Filter out selected connections
  const availableConnections = useMemo(() => {
    if (!Array.isArray(connections)) return [];
    
    return connections.filter(connection => {
      // Skip if already in group
      const inGroup = alreadyInGroup.some(g => g._id === connection._id);
      if (inGroup) return false;
      
      // Skip if already selected
      const selected = selectedConnections.some(s => s._id === connection._id);
      if (selected) return false;
      
      // Apply search filter
      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase();
        return (
          connection.name?.toLowerCase().includes(searchLower) ||
          connection.username?.toLowerCase().includes(searchLower) ||
          connection.email?.toLowerCase().includes(searchLower)
        );
      }
      
      return true;
    });
  }, [connections, selectedConnections, alreadyInGroup, searchTerm]);

  // Toggle connection selection
  const toggleConnectionSelection = (connection) => {
    setSelectedConnections(prev => {
      const isSelected = prev.some(c => c._id === connection._id);
      if (isSelected) {
        return prev.filter(c => c._id !== connection._id);
      } else {
        return [...prev, connection];
      }
    });
  };

  // Remove from selected
  const removeSelectedConnection = (connectionId) => {
    setSelectedConnections(prev => prev.filter(c => c._id !== connectionId));
  };

  // Validate groupId before sending
  const validateGroupId = () => {
    console.log('🔍 [VALIDATE] Checking groupId:', groupId);
    
    if (!groupId) {
      console.error('❌ [VALIDATE] groupId is null/undefined');
      return { valid: false, message: 'Trip ID is missing' };
    }
    
    if (groupId === 'undefined' || groupId === 'null') {
      console.error('❌ [VALIDATE] groupId is string "undefined" or "null"');
      return { valid: false, message: 'Invalid trip ID' };
    }
    
    const isValidObjectId = /^[0-9a-fA-F]{24}$/.test(groupId);
    if (!isValidObjectId) {
      console.error('❌ [VALIDATE] Invalid ObjectId format:', groupId);
      return { valid: false, message: 'Invalid trip ID format' };
    }
    
    console.log('✅ [VALIDATE] groupId is valid');
    return { valid: true, message: '' };
  };

  // Send invitations
  const sendInvitations = async () => {
    try {
      // Validate groupId first
      const validation = validateGroupId();
      if (!validation.valid) {
        alert(`❌ ${validation.message}. Please refresh the page.`);
        return;
      }
      
      if (selectedConnections.length === 0) {
        alert('Please select at least one friend to invite.');
        return;
      }

      console.log('🚀 [SEND] Sending invitations...');
      console.log('✅ [SEND] Valid groupId:', groupId);
      console.log('🔍 [SEND] Selected friends:', selectedConnections);

      // Check if any selected connection is already in the group
      const alreadyInGroupSelected = selectedConnections.filter(conn => 
        alreadyInGroup.some(g => g._id === conn._id)
      );

      if (alreadyInGroupSelected.length > 0) {
        const names = alreadyInGroupSelected.map(c => c.name).join(', ');
        alert(`❌ The following friends are already in the group: ${names}. Please remove them before sending invitations.`);
        return;
      }

      setSendingInvitations(true);
      setError('');
      setSuccessMessage('');

      // Prepare invitation data
      const invitationData = {
        userIds: selectedConnections.map(friend => friend._id).filter(id => id),
        emails: selectedConnections.map(friend => friend.email).filter(email => email),
        message: `Join my trip to ${groupDetails?.destination || 'our destination'}!`
      };

      console.log('📤 [SEND] Sending data:', { groupId, invitationData });

      // Send invitations
      const response = await inviteFriendsToGroup(groupId, invitationData);

      console.log('✅ [SEND] Invitation response:', response);

      if (response.success) {
        setSuccessMessage(response.message || 'Invitations sent successfully!');
        
        // Show detailed results if available
        if (response.invitations) {
          const successful = response.invitations.filter(i => i.success).length;
          const failed = response.invitations.filter(i => !i.success).length;
          
          if (failed > 0) {
            const failedDetails = response.invitations
              .filter(i => !i.success)
              .map(i => `${i.name || i.userId}: ${i.error}`)
              .join('\n');
            
            alert(`✅ ${successful} invitation(s) sent successfully.\n\n❌ ${failed} failed:\n${failedDetails}`);
          } else {
            alert(`✅ ${successful} invitation(s) sent successfully!`);
          }
        }
        
        // Clear selections and refresh connections
        setSelectedConnections([]);
        setTimeout(() => {
          navigate(`/groups/${groupId}`);
        }, 2000);
        
      } else {
        setError(response.message || 'Failed to send invitations');
        alert(`❌ ${response.message || 'Failed to send invitations'}`);
      }

    } catch (error) {
      console.error('❌ [SEND] Error:', error);
      
      // Better error handling
      let errorMessage = 'Failed to send invitations. ';
      
      if (error.response?.data) {
        const errorData = error.response.data;
        console.error('🔍 [SEND] Server error:', errorData);
        
        if (errorData.message?.includes('already a member')) {
          errorMessage = 'Some selected friends are already members of this group. Please select different friends.';
        } else if (errorData.message?.includes('already invited')) {
          errorMessage = 'Some selected friends are already invited. Please wait for their response.';
        } else if (errorData.message) {
          errorMessage = errorData.message;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setError(errorMessage);
      alert(`❌ ${errorMessage}`);
      
    } finally {
      setSendingInvitations(false);
    }
  };

  // Skip inviting friends
  const handleSkip = () => {
    if (groupId) {
      navigate(`/groups/${groupId}`);
    } else {
      navigate('/#/groups/my-groups');
    }
  };

  // Format user ID for display
  const formatUserId = (userId) => {
    if (!userId) return '';
    return `${userId.substring(0, 8)}...`;
  };

  // Debug function (temporary)
  const debugGroupId = () => {
    console.log('=== DEBUG GROUP ID ===');
    console.log('Current groupId:', groupId);
    console.log('Type:', typeof groupId);
    console.log('Length:', groupId?.length);
    console.log('Valid ObjectId?', /^[0-9a-fA-F]{24}$/.test(groupId || ''));
    console.log('Window location:', window.location.href);
    console.log('Hash:', window.location.hash);
    console.log('Params from useParams:', useParams());
    console.log('Current saved in localStorage:', localStorage.getItem('currentGroupId'));
    console.log('=====================');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <FaSpinner className="animate-spin text-blue-500 text-4xl mx-auto mb-4" />
          <p className="text-gray-600">Loading trip details...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-blue-50 py-8">
      <div className="max-w-6xl mx-auto px-4">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center">
              <button
                onClick={() => navigate(-1)}
                className="flex items-center text-gray-600 hover:text-gray-800 mr-6"
              >
                <FaArrowLeft className="mr-2" />
                Back
              </button>
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Invite Connections ✈️</h1>
                <p className="text-gray-600 mt-1">Invite friends to join your trip</p>
              </div>
            </div>
            
            {groupDetails && (
              <div className="text-right">
                <p className="text-sm text-gray-500">Trip to</p>
                <p className="font-bold text-gray-900">{groupDetails.destination}</p>
              </div>
            )}
          </div>

          {/* User Info */}
          <div className="bg-white rounded-xl shadow p-6 border border-gray-200 mb-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mr-4">
                  <FaUserFriends className="text-blue-600 text-xl" />
                </div>
                <div>
                  <p className="font-bold text-gray-900">
                    {user?.name || user?.username || 'User'}
                  </p>
                  <p className="text-sm text-gray-500">
                    ID: {formatUserId(user?._id || user?.id)}
                  </p>
                </div>
              </div>
              
              <div className="flex items-center gap-4">
                {/* Debug button (temporary) */}
                <button
                  onClick={debugGroupId}
                  className="px-3 py-1 bg-purple-500 text-white text-sm rounded hover:bg-purple-600"
                  title="Debug groupId"
                >
                  🔍 Debug
                </button>
                
                <button
                  onClick={loadConnections}
                  disabled={loadingConnections}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50"
                >
                  <FaSync className={loadingConnections ? 'animate-spin' : ''} />
                  Reload
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Error Messages */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl flex items-start gap-3">
            <FaExclamationTriangle className="text-red-500 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-red-700 font-medium">{error}</p>
              <button
                onClick={() => setError('')}
                className="text-red-500 text-sm hover:text-red-700 mt-1"
              >
                Dismiss
              </button>
            </div>
          </div>
        )}

        {/* Success Message */}
        {successMessage && (
          <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-xl flex items-start gap-3">
            <FaCheck className="text-green-500 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-green-700 font-medium">{successMessage}</p>
              <button
                onClick={() => setSuccessMessage('')}
                className="text-green-500 text-sm hover:text-green-700 mt-1"
              >
                Dismiss
              </button>
            </div>
          </div>
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-white rounded-xl shadow p-6 border border-gray-200">
            <div className="flex items-center">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mr-4">
                <FaUserFriends className="text-blue-600 text-xl" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{availableConnections.length}</p>
                <p className="text-sm text-gray-500">Available</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-xl shadow p-6 border border-gray-200">
            <div className="flex items-center">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mr-4">
                <FaCheck className="text-green-600 text-xl" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{selectedConnections.length}</p>
                <p className="text-sm text-gray-500">Selected</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-xl shadow p-6 border border-gray-200">
            <div className="flex items-center">
              <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center mr-4">
                <FaUsers className="text-gray-600 text-xl" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{alreadyInGroup.length}</p>
                <p className="text-sm text-gray-500">In Group</p>
              </div>
            </div>
          </div>
        </div>

        {/* Search Bar */}
        <div className="mb-6">
          <div className="relative">
            <FaSearch className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search connections by name or username..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Connections */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-xl shadow border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-bold text-gray-800">Your Connections</h3>
                <p className="text-sm text-gray-500">
                  {availableConnections.length} available to invite • {alreadyInGroup.length} already in group
                </p>
              </div>
              
              {loadingConnections ? (
                <div className="text-center py-8">
                  <FaSpinner className="animate-spin text-blue-500 text-2xl mx-auto mb-2" />
                  <p className="text-gray-600">Loading connections...</p>
                </div>
              ) : availableConnections.length === 0 ? (
                <div className="text-center py-8">
                  <FaUserFriends className="text-4xl text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-600 mb-2">
                    {connections.length === 0 
                      ? "No connections found. Follow some users to see them here."
                      : "No connections available to invite."
                    }
                  </p>
                  {connections.length > 0 && alreadyInGroup.length > 0 && (
                    <p className="text-gray-500 text-sm">
                      {alreadyInGroup.length} connection(s) are already in this group
                    </p>
                  )}
                </div>
              ) : (
                <div className="space-y-3 max-h-96 overflow-y-auto pr-2">
                  {availableConnections.map(connection => (
                    <div 
                      key={connection._id}
                      className={`flex items-center justify-between p-3 rounded-lg border transition-colors ${
                        selectedConnections.some(c => c._id === connection._id)
                          ? 'bg-blue-50 border-blue-300'
                          : 'bg-white border-gray-200 hover:bg-gray-50'
                      }`}
                    >
                      <div className="flex items-center">
                        <img
                          src={connection.profilePicture}
                          alt={connection.name}
                          className="w-10 h-10 rounded-full mr-3"
                          onError={(e) => {
                            e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(connection.name)}&background=3b82f6&color=fff&size=128`;
                          }}
                        />
                        <div>
                          <p className="font-medium text-gray-800">{connection.name}</p>
                          <p className="text-sm text-gray-500">
                            {connection.username && `@${connection.username}`}
                            {connection.city && ` • ${connection.city}`}
                          </p>
                        </div>
                      </div>
                      
                      <button
                        onClick={() => toggleConnectionSelection(connection)}
                        className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                          selectedConnections.some(c => c._id === connection._id)
                            ? 'bg-blue-500 text-white hover:bg-blue-600'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        {selectedConnections.some(c => c._id === connection._id) ? 'Selected' : 'Select'}
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          
          {/* Right Column - Selected & Actions */}
          <div>
            {/* Selected Connections */}
            <div className="bg-white rounded-xl shadow border border-gray-200 p-6 mb-6">
              <h3 className="font-bold text-gray-800 mb-4">Selected to Invite</h3>
              
              {selectedConnections.length === 0 ? (
                <div className="text-center py-8">
                  <FaEnvelope className="text-4xl text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-600">No friends selected yet</p>
                </div>
              ) : (
                <div className="space-y-3 max-h-64 overflow-y-auto pr-2">
                  {selectedConnections.map(connection => (
                    <div 
                      key={connection._id}
                      className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg"
                    >
                      <div className="flex items-center">
                        <img
                          src={connection.profilePicture}
                          alt={connection.name}
                          className="w-8 h-8 rounded-full mr-3"
                          onError={(e) => {
                            e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(connection.name)}&background=10b981&color=fff&size=128`;
                          }}
                        />
                        <div>
                          <p className="font-medium text-gray-800">{connection.name}</p>
                          <p className="text-xs text-gray-500">
                            {connection.username && `@${connection.username}`}
                          </p>
                        </div>
                      </div>
                      
                      <button
                        onClick={() => removeSelectedConnection(connection._id)}
                        className="text-red-500 hover:text-red-700"
                      >
                        <FaTimes />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            {/* Action Buttons */}
            <div className="space-y-4">
              <button
                onClick={sendInvitations}
                disabled={selectedConnections.length === 0 || sendingInvitations || !groupId}
                className={`w-full py-3 rounded-lg font-bold flex items-center justify-center gap-2 transition-colors ${
                  selectedConnections.length === 0 || sendingInvitations || !groupId
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-blue-500 text-white hover:bg-blue-600'
                }`}
              >
                {sendingInvitations ? (
                  <>
                    <FaSpinner className="animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <FaEnvelope />
                    Send {selectedConnections.length} Invitation{selectedConnections.length !== 1 ? 's' : ''}
                  </>
                )}
              </button>
              
              <button
                onClick={handleSkip}
                className="w-full py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-bold"
              >
                Skip for Now
              </button>
            </div>
          </div>
        </div>

        {/* How It Works */}
        <div className="mt-8 bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl p-6 border border-blue-200">
          <h4 className="font-bold text-blue-800 mb-4 flex items-center">
            <FaInfoCircle className="mr-2" />
            How It Works
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 text-sm text-blue-700">
            <div className="flex flex-col items-center text-center">
              <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center mb-2">
                <span className="text-blue-600 font-bold">1</span>
              </div>
              <p className="font-medium">Select people</p>
              <p className="text-xs">From your followers + following list</p>
            </div>
            <div className="flex flex-col items-center text-center">
              <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center mb-2">
                <span className="text-blue-600 font-bold">2</span>
              </div>
              <p className="font-medium">Check availability</p>
              <p className="text-xs">Friends in group cannot be selected</p>
            </div>
            <div className="flex flex-col items-center text-center">
              <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center mb-2">
                <span className="text-blue-600 font-bold">3</span>
              </div>
              <p className="font-medium">Send invitations</p>
              <p className="text-xs">Selected users get trip invitation</p>
            </div>
            <div className="flex flex-col items-center text-center">
              <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center mb-2">
                <span className="text-blue-600 font-bold">4</span>
              </div>
              <p className="font-medium">Wait for response</p>
              <p className="text-xs">They can accept or decline</p>
            </div>
            <div className="flex flex-col items-center text-center">
              <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center mb-2">
                <span className="text-blue-600 font-bold">5</span>
              </div>
              <p className="font-medium">Join automatically</p>
              <p className="text-xs">Accepted users join your group</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InviteFriendsToTrip;